/**
 * Gestão de Pedidos — status do financeiro por pedido (rodada 5, 2026-09-29).
 *
 * Double check MANUAL do financeiro: Não visto (padrão) / Visto / Pendência,
 * independente de alguém ter aberto o pedido e SEPARADO da conciliação do
 * pedido (que continua onde estava, na aba Financeiro). Pendência exige nota.
 * Wire `finance_review` = `{ status, note, updated_at, updated_by_name }`
 * (sem linha = NAO_VISTO); `null` = o setor financeiro não é visível para o
 * usuário (nada aparece). Trocar = `POST /pedidos/:id/financeiro/status`
 * (`rmas:finance:update`), que responde `{ finance_review }`.
 *
 * Duas portas para a mesma edição (`FinanceReviewEditor`):
 *   · lista — `FinanceReviewCell`: o chip abre um menu pequeno (portal, fora
 *     do link da linha) para trocar SEM abrir o pedido;
 *   · detalhe — `FinanceReviewPanel`, na aba Financeiro.
 * Cores: Não visto = cinza discreto, Visto = verde, Pendência = âmbar.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CaretDown } from 'shared/icons';
import { Panel } from '../../components/rma/rmaShared';
import { useToast } from '../../components/ui/Toast';
import {
    CASE_REVIEW_NOTE_MAX,
    FINANCE_REVIEW_STATUSES,
    FinanceReviewMeta,
    TONE_CLASSES,
} from '../../constants/pedidoTags';
import api from '../../services/api';
import { ActorLine, HasRequiredNote, NOTE_FIELD_CLASSES } from './caseReview';
import { PRIMARY_BUTTON_CLASSES } from './listParts';

const META_TEXT = 'text-[11px] text-mono-400 dark:text-mono-500';
const ERROR_TEXT = 'text-[11px] text-danger-600 dark:text-danger-400';
const MENU_WIDTH = 288;
// Altura que o menu pode ocupar abaixo do chip (com a nota da pendência aberta).
const MENU_ROOM = 320;

/** `title` do chip: status, nota e quem/quando. */
function reviewTitle(review) {
    const meta = FinanceReviewMeta(review?.status);
    const actor = ActorLine(review?.updated_by_name, review?.updated_at);
    return [`Financeiro: ${meta.label}`, review?.note ? `nota: ${review.note}` : null, actor || null]
        .filter(Boolean)
        .join('\n');
}

/** Chip do status do financeiro (sem ação). `trailing` = ícone do menu. */
export const FinanceReviewChip = ({ review, trailing = null }) => {
    const meta = FinanceReviewMeta(review?.status);
    const classes = TONE_CLASSES[meta.tone] || TONE_CLASSES.neutral;
    const discreet = meta.tone === 'neutral';
    return (
        <span
            title={reviewTitle(review)}
            className={`inline-flex max-w-full items-center gap-1 whitespace-nowrap px-2 py-0.5 text-[11px] ${
                discreet
                    ? 'bg-transparent text-mono-400 ring-1 ring-inset ring-mono-200 dark:text-mono-500 dark:ring-mono-800'
                    : `font-semibold ${classes.chip} ${classes.ring}`
            }`}
        >
            <span className={`h-1.5 w-1.5 shrink-0 ${classes.dot}`} />
            <span className="min-w-0 truncate">{meta.label}</span>
            {trailing}
        </span>
    );
};

/**
 * Edição do status: três botões (o atual marcado). Não visto e Visto salvam
 * na hora; Pendência abre a nota OBRIGATÓRIA (pré-preenchida com a nota atual
 * quando já é pendência) e "Salvar pendência". `onSubmit({ status, note })`
 * devolve promise; `true` = salvou.
 */
export const FinanceReviewEditor = ({ review, busy = false, onSubmit }) => {
    const current = review?.status || 'NAO_VISTO';
    const [pendingOpen, setPendingOpen] = useState(false);
    const [note, setNote] = useState('');
    const [error, setError] = useState('');

    const choose = async (status) => {
        setError('');
        if (status === 'PENDENCIA') {
            setNote(current === 'PENDENCIA' ? review?.note || '' : '');
            setPendingOpen(true);
            return;
        }
        setPendingOpen(false);
        if (status === current) {
            return;
        }
        await onSubmit({ status, note: null });
    };

    const savePending = async () => {
        if (!HasRequiredNote(note)) {
            setError('Descreva a pendência (pelo menos 3 caracteres).');
            return;
        }
        const ok = await onSubmit({ status: 'PENDENCIA', note: note.trim() });
        if (ok) {
            setPendingOpen(false);
            setNote('');
        }
    };

    return (
        <div className="flex flex-col gap-2">
            <div className="grid grid-cols-3 gap-px border border-mono-200 bg-mono-200 dark:border-mono-800 dark:bg-mono-800">
                {FINANCE_REVIEW_STATUSES.map((status) => {
                    const meta = FinanceReviewMeta(status);
                    const active = status === current;
                    const opening = status === 'PENDENCIA' && pendingOpen;
                    return (
                        <button
                            key={status}
                            type="button"
                            aria-pressed={active}
                            disabled={busy}
                            onClick={() => choose(status)}
                            className={`flex items-center justify-center gap-1.5 px-2 py-1.5 text-[12px] transition-colors disabled:opacity-50 ${
                                active || opening
                                    ? 'bg-black text-white dark:bg-white dark:text-black'
                                    : 'bg-white text-mono-600 hover:bg-mono-50 dark:bg-mono-950 dark:text-mono-300 dark:hover:bg-mono-900'
                            }`}
                        >
                            <span className={`h-1.5 w-1.5 shrink-0 ${TONE_CLASSES[meta.tone]?.dot || ''}`} />
                            {meta.label}
                        </button>
                    );
                })}
            </div>
            {pendingOpen ? (
                <div className="flex flex-col gap-1.5">
                    <textarea
                        // biome-ignore lint/a11y/noAutofocus: aberto por clique explícito em "Pendência"
                        autoFocus
                        value={note}
                        rows={3}
                        maxLength={CASE_REVIEW_NOTE_MAX}
                        disabled={busy}
                        placeholder="Qual é a pendência? (obrigatório)"
                        aria-label="Nota da pendência"
                        aria-invalid={error ? true : undefined}
                        onChange={(e) => {
                            setNote(e.target.value);
                            if (error) {
                                setError('');
                            }
                        }}
                        className={NOTE_FIELD_CLASSES}
                    />
                    <span className="flex flex-wrap items-center gap-3">
                        <button
                            type="button"
                            onClick={savePending}
                            disabled={busy || !note.trim()}
                            className={PRIMARY_BUTTON_CLASSES}
                        >
                            {busy ? 'Salvando…' : 'Salvar pendência'}
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                setPendingOpen(false);
                                setError('');
                            }}
                            disabled={busy}
                            className="text-[11px] text-mono-500 underline underline-offset-2 hover:text-black dark:text-mono-400 dark:hover:text-white"
                        >
                            cancelar
                        </button>
                    </span>
                    {error ? (
                        <span role="alert" className={ERROR_TEXT}>
                            {error}
                        </span>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
};

/**
 * Gravação do status com toast: devolve `true` quando salvou. `onSaved`
 * recebe o `finance_review` do backend (ou o enviado, se a resposta vier sem).
 */
function useFinanceReviewSave(orderId, onSaved) {
    const toast = useToast();
    const [busy, setBusy] = useState(false);
    const save = useCallback(
        async ({ status, note }) => {
            setBusy(true);
            try {
                const result = await api.setPedidoFinanceStatus(orderId, { status, note });
                const saved =
                    result?.finance_review && typeof result.finance_review === 'object'
                        ? result.finance_review
                        : { status, note: note || null, updated_at: new Date().toISOString(), updated_by_name: null };
                toast.success(`Financeiro: ${FinanceReviewMeta(saved.status).label}.`);
                await onSaved?.(saved);
                return true;
            } catch (err) {
                toast.error(err?.message || 'Não foi possível salvar o status do financeiro.');
                return false;
            } finally {
                setBusy(false);
            }
        },
        [orderId, onSaved, toast],
    );
    return { busy, save };
}

/**
 * Célula "Financeiro" da lista. Sem `rmas:finance:update`: só o chip. Com a
 * permissão: o chip é um botão que abre o menu (portal no body — fora do
 * `<Link>` da linha e do `overflow` da tabela) com a mesma edição do
 * detalhe; clique fora, Esc ou rolagem fecham. `onSaved(review)` atualiza a
 * linha localmente.
 */
export const FinanceReviewCell = ({ orderId, review, canEdit = false, onSaved = null }) => {
    const [open, setOpen] = useState(false);
    const [pos, setPos] = useState(null);
    const anchorRef = useRef(null);
    const menuRef = useRef(null);
    const handleSaved = useCallback(
        async (saved) => {
            setOpen(false);
            await onSaved?.(saved);
        },
        [onSaved],
    );
    const { busy, save } = useFinanceReviewSave(orderId, handleSaved);

    useEffect(() => {
        if (!open) {
            return undefined;
        }
        const onPointer = (event) => {
            if (menuRef.current?.contains(event.target) || anchorRef.current?.contains(event.target)) {
                return;
            }
            setOpen(false);
        };
        const onKey = (event) => {
            if (event.key === 'Escape') {
                setOpen(false);
            }
        };
        const onScroll = (event) => {
            if (menuRef.current?.contains(event.target)) {
                return;
            }
            setOpen(false);
        };
        document.addEventListener('mousedown', onPointer);
        document.addEventListener('keydown', onKey);
        window.addEventListener('scroll', onScroll, true);
        window.addEventListener('resize', onScroll);
        return () => {
            document.removeEventListener('mousedown', onPointer);
            document.removeEventListener('keydown', onKey);
            window.removeEventListener('scroll', onScroll, true);
            window.removeEventListener('resize', onScroll);
        };
    }, [open]);

    if (!review || typeof review !== 'object') {
        return <span className="text-xs text-mono-300 dark:text-mono-700">—</span>;
    }
    if (!canEdit) {
        return (
            <span className="flex min-w-0">
                <FinanceReviewChip review={review} />
            </span>
        );
    }

    const toggle = (event) => {
        // A linha inteira é um <Link>: o clique no chip não navega.
        event.preventDefault();
        event.stopPropagation();
        if (open) {
            setOpen(false);
            return;
        }
        const rect = anchorRef.current?.getBoundingClientRect();
        if (rect) {
            const left = Math.max(8, Math.min(rect.left, window.innerWidth - MENU_WIDTH - 8));
            // Perto do rodapé da janela o menu cresce para CIMA (ancorado pelo
            // `bottom`) — a nota da pendência aumenta a altura dele.
            const openUp = rect.bottom + MENU_ROOM > window.innerHeight;
            setPos(
                openUp
                    ? { left, bottom: Math.max(8, window.innerHeight - rect.top + 4) }
                    : { left, top: rect.bottom + 4 },
            );
        }
        setOpen(true);
    };

    return (
        <span className="flex min-w-0">
            <button
                ref={anchorRef}
                type="button"
                onClick={toggle}
                aria-haspopup="dialog"
                aria-expanded={open}
                title="Trocar o status do financeiro"
                className="min-w-0 max-w-full"
            >
                <FinanceReviewChip review={review} trailing={<CaretDown size={10} className="shrink-0 opacity-60" />} />
            </button>
            {open && pos
                ? createPortal(
                      // O menu vive no body, mas o evento sintético do React ainda sobe
                      // pela árvore até o <Link> da linha — para aqui.
                      <div
                          ref={menuRef}
                          role="dialog"
                          aria-label="Status do financeiro"
                          onClick={(event) => event.stopPropagation()}
                          style={{ position: 'fixed', ...pos, width: MENU_WIDTH }}
                          className="z-50 flex flex-col gap-2 border border-mono-300 bg-white p-3 dark:border-mono-700 dark:bg-mono-950"
                      >
                          <p className="text-[10px] font-bold uppercase tracking-widest text-mono-400 dark:text-mono-600">
                              Status do financeiro
                          </p>
                          {review.status === 'PENDENCIA' && review.note ? (
                              <p className="whitespace-pre-wrap break-words border-l-2 border-l-amber-500 pl-2 text-[12px] text-mono-700 dark:text-mono-200">
                                  {review.note}
                              </p>
                          ) : null}
                          <FinanceReviewEditor review={review} busy={busy} onSubmit={save} />
                          {review.updated_at || review.updated_by_name ? (
                              <p className={META_TEXT}>
                                  {ActorLine(review.updated_by_name, review.updated_at).replace(/^por/, 'marcado por')}
                              </p>
                          ) : null}
                      </div>,
                      document.body,
                  )
                : null}
        </span>
    );
};

/**
 * Bloco "Status do financeiro" da aba Financeiro do detalhe: chip, nota e
 * quem/quando, e a edição para quem tem `rmas:finance:update`. Separado da
 * conciliação do pedido (bloco próprio, que não muda). Sem `review` (setor
 * financeiro invisível ou backend antigo) → não renderiza.
 */
export const FinanceReviewPanel = ({ orderId, review, canEdit = false, onSaved = null }) => {
    const { busy, save } = useFinanceReviewSave(orderId, onSaved);
    if (!review || typeof review !== 'object') {
        return null;
    }
    const actor = ActorLine(review.updated_by_name, review.updated_at);
    const pending = review.status === 'PENDENCIA';
    return (
        <Panel
            title="Status do financeiro"
            className={pending ? 'border-l-4 border-l-amber-500' : ''}
            action={<span className={META_TEXT}>double check manual · separado da conciliação</span>}
        >
            <div className="flex flex-wrap items-center gap-2">
                <FinanceReviewChip review={review} />
                <span className={META_TEXT}>{actor ? `marcado ${actor}` : 'nunca marcado'}</span>
            </div>
            {review.note ? (
                <p
                    className={`whitespace-pre-wrap break-words border-l-2 pl-3 text-[13px] text-mono-800 dark:text-mono-100 ${
                        pending ? 'border-l-amber-500' : 'border-l-mono-300 dark:border-l-mono-700'
                    }`}
                >
                    {review.note}
                </p>
            ) : null}
            {canEdit ? (
                <div className="max-w-md">
                    <FinanceReviewEditor review={review} busy={busy} onSubmit={save} />
                </div>
            ) : (
                <span className={META_TEXT}>trocar exige rmas:finance:update</span>
            )}
        </Panel>
    );
};
